import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import api, { API_BASE } from '../utils/api';
import { normalizeBackendUrl } from '../utils/apiBase';
import { setSession, clearSession, getTokenSync, getUserJsonSync } from '../utils/authStorage';
import { normalizeAuthUser } from '../utils/authUser';
import { isMobileContainer } from '../utils/device';
import {
  hasPermission as checkPermission,
  canAccessNavPath,
  canNavigateToPath,
  isNavPathLocked,
  canAccessSettings,
} from '../utils/navPermissions';
import {
  canViewFeature as checkViewFeature,
  canManageFeature as checkManageFeature,
} from '../utils/featurePermissions';

const AuthContext = createContext(null);

function readCachedUser() {
  const raw = getUserJsonSync();
  if (!raw) return null;
  try {
    return normalizeAuthUser(JSON.parse(raw));
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  /** True while we have a token and are loading /auth/me (or initial session reconcile). */
  const [bootstrapping, setBootstrapping] = useState(() => !!getTokenSync());

  useEffect(() => {
    const onAuthCleared = () => {
      setUser(null);
      setBootstrapping(false);
    };
    window.addEventListener('schoolerp:auth-cleared', onAuthCleared);
    return () => window.removeEventListener('schoolerp:auth-cleared', onAuthCleared);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const token = getTokenSync();

    if (!token) {
      setUser(null);
      setBootstrapping(false);
      return undefined;
    }

    (async () => {
      try {
        const res = await api.get('/auth/me');
        const body = res.data;
        const payload = body?.data ?? body;
        const newToken = payload?.token;
        const userPayload = payload?.user ?? payload;
        if (!cancelled && userPayload && typeof userPayload === 'object' && userPayload.id != null) {
          const normalized = normalizeAuthUser(userPayload);
          const sessionToken = newToken || token;
          await setSession(sessionToken, JSON.stringify(normalized));
          setUser(normalized);
        }
      } catch (e) {
        if (!cancelled) {
          const cached = readCachedUser();
          if (cached?.id) {
            setUser(cached);
          } else {
            setUser(null);
          }
        }
      } finally {
        if (!cancelled) {
          setBootstrapping(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (identifier, password) => {
    try {
      const payload = { email: identifier.trim(), password };
      if (isMobileContainer()) {
        payload.client = 'mobile';
      }
      const response = await api.post('/auth/login', payload);
      const body = response.data;
      const data = body?.data ?? body;
      const token = data?.token;
      const loginUser = data?.user;
      if (!token || !loginUser) {
        return { success: false, message: body?.message || 'Invalid response from server' };
      }
      const normalized = normalizeAuthUser(loginUser);
      await setSession(token, JSON.stringify(normalized));
      setUser(normalized);
      setBootstrapping(false);
      return { success: true, user: normalized };
    } catch (error) {
      if (error.response?.status === 404) {
        return {
          success: false,
          message: `API not found at ${API_BASE}/api (HTTP 404). Deploy backend to that host with document root = backend/public/, or set REACT_APP_BACKEND_URL in frontend/.env and restart npm start.`,
        };
      }
      if (error.code === 'ERR_NETWORK' || !error.response) {
        const configured = normalizeBackendUrl(process.env.REACT_APP_BACKEND_URL);
        const isHttpsApi = /^https:\/\//i.test(API_BASE || '');
        const message = isHttpsApi
          ? `Cannot reach API at ${API_BASE}. Host down, SSL issue, or CORS — check ${configured || 'REACT_APP_BACKEND_URL'}, DNS, and rebuild after .env change.`
          : 'Cannot reach backend. Start XAMPP Apache or: cd backend && php spark serve --port=8080';
        return { success: false, message };
      }
      const msg = error.response?.data?.message || error.response?.data?.error || 'Login failed';
      return { success: false, message: msg };
    }
  };

  const logout = () => {
    clearSession().catch(() => {});
    setUser(null);
    setBootstrapping(false);
  };

  const applyAcademySsoSession = useCallback(async ({ token, user: ssoUser }) => {
    if (!token || !ssoUser) return;
    const normalized = normalizeAuthUser(ssoUser);
    await setSession(token, JSON.stringify(normalized));
    setUser(normalized);
    setBootstrapping(false);
  }, []);

  const patchUser = useCallback((partial) => {
    setUser((prev) => {
      if (!prev || !partial || typeof partial !== 'object') return prev;
      const next = normalizeAuthUser({ ...prev, ...partial });
      const token = getTokenSync();
      if (token) {
        setSession(token, JSON.stringify(next)).catch(() => {});
      }
      return next;
    });
  }, []);

  const refreshUser = useCallback(async () => {
    const token = getTokenSync();
    if (!token) return null;
    try {
      const res = await api.get('/auth/me');
      const body = res.data;
      const payload = body?.data ?? body;
      const newToken = payload?.token;
      const userPayload = payload?.user ?? payload;
      if (userPayload && typeof userPayload === 'object' && userPayload.id != null) {
        const normalized = normalizeAuthUser(userPayload);
        const sessionToken = newToken || token;
        await setSession(sessionToken, JSON.stringify(normalized));
        setUser(normalized);
        return normalized;
      }
    } catch {
      return null;
    }
    return null;
  }, []);

  const switchBranch = useCallback(async (branchId) => {
    try {
      const res = await api.post('/auth/switch-branch', { branch_id: branchId });
      const body = res.data;
      const payload = body?.data ?? body;
      const newToken = payload?.token;
      const userPayload = payload?.user;
      if (!newToken || !userPayload) {
        return { success: false, message: body?.message || 'Invalid response from server' };
      }
      const normalized = normalizeAuthUser(userPayload);
      await setSession(newToken, JSON.stringify(normalized));
      setUser(normalized);
      window.dispatchEvent(new CustomEvent('schoolerp:branch-switched', { detail: { branchId } }));
      return { success: true, user: normalized };
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error || 'Failed to switch branch';
      return { success: false, message: msg };
    }
  }, []);

  const enterSchoolObserver = useCallback(async (schoolId) => {
    try {
      const res = await api.post('/auth/enter-school-observer', { school_id: Number(schoolId) });
      const body = res.data;
      const payload = body?.data ?? body;
      const newToken = payload?.token;
      const userPayload = payload?.user;
      if (!newToken || !userPayload) {
        return { success: false, message: body?.message || 'Invalid response from server' };
      }
      const normalized = normalizeAuthUser(userPayload);
      await setSession(newToken, JSON.stringify(normalized));
      flushSync(() => {
        setUser(normalized);
      });
      window.dispatchEvent(new CustomEvent('schoolerp:school-observer-changed', { detail: { active: true } }));
      return { success: true, user: normalized };
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error || 'Failed to open school view';
      return { success: false, message: msg };
    }
  }, []);

  const exitSchoolObserver = useCallback(async () => {
    try {
      const res = await api.post('/auth/exit-school-observer');
      const body = res.data;
      const payload = body?.data ?? body;
      const newToken = payload?.token;
      const userPayload = payload?.user;
      if (!newToken || !userPayload) {
        return { success: false, message: body?.message || 'Invalid response from server' };
      }
      const normalized = normalizeAuthUser(userPayload);
      await setSession(newToken, JSON.stringify(normalized));
      flushSync(() => {
        setUser(normalized);
      });
      window.dispatchEvent(new CustomEvent('schoolerp:school-observer-changed', { detail: { active: false } }));
      return { success: true, user: normalized };
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error || 'Failed to exit school view';
      return { success: false, message: msg };
    }
  }, []);

  const isAuthenticated = !!user;
  const isAdmin = user?.role_slug === 'school_admin';
  const isSchoolObserver = !!(user?.role_slug === 'super_admin' && user?.school_observer_mode);
  const activeBranchId = user?.active_branch_id ?? null;
  const activeBranchName = user?.active_branch_name ?? null;

  const hasPermission = useCallback(
    (permission) => checkPermission(user, permission),
    [user]
  );

  const canAccessPath = useCallback(
    (path) => canAccessNavPath(user, path),
    [user]
  );

  const canNavigatePath = useCallback(
    (path) => canNavigateToPath(user, path),
    [user]
  );

  const isModuleLocked = useCallback(
    (path) => isNavPathLocked(user, path),
    [user]
  );

  const canOpenSettings = useCallback(
    () => canAccessSettings(user),
    [user]
  );

  const canViewFeature = useCallback(
    (featureKey) => checkViewFeature(user, featureKey),
    [user]
  );

  const canManageFeature = useCallback(
    (featureKey) => checkManageFeature(user, featureKey),
    [user]
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        loading: false,
        bootstrapping,
        isAuthenticated,
        isAdmin,
        hasPermission,
        canAccessPath,
        canNavigatePath,
        isModuleLocked,
        canOpenSettings,
        canViewFeature,
        canManageFeature,
        applyAcademySsoSession,
        patchUser,
        refreshUser,
        switchBranch,
        enterSchoolObserver,
        exitSchoolObserver,
        isSchoolObserver,
        activeBranchId,
        activeBranchName,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
